import { useEffect, useMemo, useState, type DragEvent, type FormEvent } from 'react';
import { statusText, taskProgress, uuidv7, type Board, type BoardColumn, type Task } from '@machs/shared';
import { userAvatarUrl } from '../lib/api';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { Icon } from '../lib/icons';
import { avatarGradient, initials, shortDate } from '../lib/format';
import { Avatar, Dialog, Popover } from './ui';

/** Spalten, mit denen ein neues Board beginnt. */
const START_COLUMNS = ['Zu erledigen', 'In Arbeit', 'Erledigt'];

/** Abstand zwischen zwei Karten in der Sortierung; dazwischen bleibt Platz. */
const STEP = 1000;

function remembered(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Board (Kanban) eines Projekts. Die Aufgabe selbst bleibt der Chat: ein Klick
 * auf eine Karte öffnet den Verlauf.
 */
export function BoardView({ projectId, onOpenTask, onNewTask, onNewSubtask }: {
  projectId: string;
  onOpenTask: (id: string) => void;
  onNewTask: () => void;
  onNewSubtask: (parentId: string) => void;
}) {
  const { client, me, version } = useSync();
  const plan = usePlan();
  const project = client.data.project.get(projectId);

  const boards = useMemo(
    () => [...client.data.board.values()]
      .filter((b) => b.projectId === projectId && !b.archivedAt)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [client, version, projectId],
  );

  const [pick, setPick] = useState<string | null>(() => remembered(`machs.board.${projectId}`));
  const board = boards.find((b) => b.id === pick) ?? boards[0] ?? null;
  useEffect(() => {
    try {
      if (board) localStorage.setItem(`machs.board.${projectId}`, board.id);
    } catch {
      // nur Komfort
    }
  }, [board, projectId]);

  const [showDone, setShowDone] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ column: string | null; before: string | null } | null>(null);
  const [rename, setRename] = useState<{ kind: 'board' | 'column' | 'newBoard' | 'newColumn'; id?: string; value: string } | null>(null);

  const tasks = useMemo(
    () => [...client.data.task.values()].filter((t) => t.projectId === projectId),
    [client, version, projectId],
  );

  if (!project) return null;

  if (!board) {
    return (
      <div className="flex h-full flex-col">
        <BoardHead
          project={project.name}
          boards={boards}
          board={null}
          onPick={setPick}
          onNew={() => setRename({ kind: 'newBoard', value: '' })}
        />
        <div className="grid flex-1 place-items-center p-8 text-center">
          <div className="max-w-[360px]">
            <div className="mb-4 flex justify-center text-[var(--dim)]"><Icon name="board" size={44} /></div>
            <h2 className="display m-0 text-[20px]">Noch kein Board</h2>
            <p className="mt-2 text-[var(--muted)]">
              Auf einem Board liegen die Aufgaben dieses Projekts in Spalten. Du kannst mehrere Boards anlegen, etwa je Bauabschnitt oder Kunde.
            </p>
            <button className="btn btn-primary mt-4" onClick={() => void createBoard('Board')}>Board anlegen</button>
          </div>
        </div>
        {rename && <NameDialog state={rename} onClose={() => setRename(null)} onSave={(name) => void createBoard(name)} />}
      </div>
    );
  }

  async function createBoard(name: string) {
    const count = [...client.data.board.values()].filter((b) => b.projectId === projectId && !b.archivedAt).length;
    if (!plan.allow('boardsPerProject', count + 1)) return;
    const id = await client.saveBoard({
      projectId,
      name: name.trim() || 'Board',
      columns: START_COLUMNS.map((c) => ({ id: uuidv7(), name: c, color: null })),
    });
    setPick(id);
    setRename(null);
  }

  const columns = board.columns;
  const colOf = (t: Task) => (columns.some((c) => c.id === t.columnId) ? t.columnId : (columns[0]?.id ?? null));
  /** Karten einer Spalte; `null` steht für die Aufgaben, die noch auf keinem Board liegen. */
  const cardsOf = (columnId: string | null) =>
    tasks
      .filter((t) => (showDone || t.status !== 'erledigt') && (columnId === null ? !t.boardId : t.boardId === board.id && colOf(t) === columnId))
      .sort((a, b) => a.boardOrder - b.boardOrder || (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));

  /** Platz zwischen den Nachbarn, damit die Karte genau dort landet. */
  function orderFor(columnId: string | null, before: string | null): number {
    const list = cardsOf(columnId).filter((t) => t.id !== dragId);
    const at = before ? list.findIndex((t) => t.id === before) : list.length;
    const index = at < 0 ? list.length : at;
    const prev = index > 0 ? list[index - 1]!.boardOrder : null;
    const next = index < list.length ? list[index]!.boardOrder : null;
    if (prev === null && next === null) return 0;
    if (prev === null) return next! - STEP;
    if (next === null) return prev + STEP;
    return (prev + next) / 2;
  }

  function drop(columnId: string | null, before: string | null) {
    const id = dragId;
    setOver(null);
    setDragId(null);
    if (!id) return;
    const order = orderFor(columnId, before);
    void client.moveTask(id, columnId === null ? { boardId: null } : { boardId: board!.id, columnId, boardOrder: order });
  }

  function saveColumns(next: BoardColumn[]) {
    void client.saveBoard({ id: board!.id, projectId, name: board!.name, columns: next });
  }

  function addColumn(name: string) {
    saveColumns([...columns, { id: uuidv7(), name: name.trim() || 'Neue Spalte', color: null }]);
    setRename(null);
  }

  function moveColumn(id: string, dir: -1 | 1) {
    const i = columns.findIndex((c) => c.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[i], next[j]] = [next[j]!, next[i]!];
    saveColumns(next);
  }

  function removeColumn(id: string) {
    const rest = columns.filter((c) => c.id !== id);
    // Die Karten der Spalte bleiben auf des Boards und rücken in die erste Spalte
    for (const t of tasks) {
      if (t.boardId === board!.id && t.columnId === id) {
        void client.moveTask(t.id, rest[0] ? { boardId: board!.id, columnId: rest[0].id, boardOrder: t.boardOrder } : { boardId: null });
      }
    }
    saveColumns(rest);
  }

  const lanes: { id: string | null; name: string }[] = [{ id: null, name: 'Ohne Board' }, ...columns.map((c) => ({ id: c.id, name: c.name }))];

  return (
    <div className="flex h-full flex-col">
      <BoardHead
        project={project.name}
        boards={boards}
        board={board}
        onPick={setPick}
        onNew={() => setRename({ kind: 'newBoard', value: '' })}
        onRename={() => setRename({ kind: 'board', id: board.id, value: board.name })}
        onDelete={() => void client.deleteBoard(board.id)}
        onAddColumn={() => setRename({ kind: 'newColumn', value: '' })}
        showDone={showDone}
        onShowDone={() => setShowDone((v) => !v)}
        onNewTask={onNewTask}
      />

      <div className="board-lanes scroll">
        {lanes.map((lane) => {
          const cards = cardsOf(lane.id);
          return (
            <section
              key={lane.id ?? 'ohne'}
              className={`board-lane ${lane.id === null ? 'muted' : ''} ${over?.column === lane.id ? 'over' : ''}`}
              onDragOver={(e: DragEvent) => {
                if (!dragId) return;
                e.preventDefault();
                setOver({ column: lane.id, before: null });
              }}
              onDrop={(e: DragEvent) => (e.preventDefault(), drop(lane.id, over?.column === lane.id ? over.before : null))}
            >
              <header className="board-lanehead">
                <b className="truncate text-[14px]">{lane.name}</b>
                <span className="n">{cards.length}</span>
                {lane.id !== null && (
                  <Popover
                    align="right"
                    width={220}
                    trigger={({ toggle, open }) => (
                      <button className="iconbtn plain ml-auto" style={{ width: 26, height: 26 }} aria-label={`Spalte ${lane.name}`} aria-expanded={open} onClick={toggle}>
                        <Icon name="more" size={15} />
                      </button>
                    )}
                  >
                    {(close) => (
                      <>
                        <button className="item" onClick={() => (setRename({ kind: 'column', id: lane.id!, value: lane.name }), close())}>
                          <Icon name="text" size={18} />Umbenennen
                        </button>
                        <button className="item" onClick={() => (moveColumn(lane.id!, -1), close())}><Icon name="back" size={18} />Nach links</button>
                        <button className="item" onClick={() => (moveColumn(lane.id!, 1), close())}><Icon name="chev" size={18} />Nach rechts</button>
                        <button className="item text-[var(--problem)]" onClick={() => (removeColumn(lane.id!), close())}>
                          <Icon name="trash" size={18} />Spalte löschen
                        </button>
                      </>
                    )}
                  </Popover>
                )}
              </header>

              <div className="board-cards">
                {cards.map((t) => (
                  <Card
                    key={t.id}
                    task={t}
                    dragging={dragId === t.id}
                    marked={over?.column === lane.id && over.before === t.id}
                    lanes={lanes}
                    current={lane.id}
                    onOpen={() => onOpenTask(t.id)}
                    onNewSubtask={t.parentId ? undefined : () => onNewSubtask(t.id)}
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => (setDragId(null), setOver(null))}
                    onDragOverCard={(before) => dragId && dragId !== t.id && setOver({ column: lane.id, before })}
                    onMove={(to) => void client.moveTask(t.id, to === null ? { boardId: null } : { boardId: board.id, columnId: to, boardOrder: orderFor(to, null) })}
                  />
                ))}
                {cards.length === 0 && (
                  <p className="m-0 px-1 py-6 text-center text-[13px] text-[var(--dim)]">
                    {lane.id === null ? 'Alle Aufgaben liegen auf einem Board' : 'Karten hierher ziehen'}
                  </p>
                )}
              </div>
            </section>
          );
        })}

        <button className="board-add" onClick={() => setRename({ kind: 'newColumn', value: '' })}>
          <Icon name="plus" size={18} />Spalte
        </button>
      </div>

      {rename && (
        <NameDialog
          state={rename}
          onClose={() => setRename(null)}
          onSave={(name) => {
            if (rename.kind === 'newBoard') return void createBoard(name);
            if (rename.kind === 'newColumn') return addColumn(name);
            if (rename.kind === 'board') {
              void client.saveBoard({ id: board.id, projectId, name });
              return setRename(null);
            }
            saveColumns(columns.map((c) => (c.id === rename.id ? { ...c, name } : c)));
            setRename(null);
          }}
        />
      )}
    </div>
  );
}

function BoardHead(p: {
  project: string;
  boards: Board[];
  board: Board | null;
  onPick: (id: string) => void;
  onNew: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onAddColumn?: () => void;
  showDone?: boolean;
  onShowDone?: () => void;
  onNewTask?: () => void;
}) {
  return (
    <header className="board-head">
      <div className="min-w-0">
        <small className="block truncate text-[12.5px] text-[var(--muted)]">{p.project}</small>
        <Popover
          width={280}
          trigger={({ toggle, open }) => (
            <button className="flex items-center gap-1.5" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
              <b className="display truncate text-[18px]">{p.board?.name ?? 'Boards'}</b>
              <Icon name="down" size={15} />
            </button>
          )}
        >
          {(close) => (
            <>
              {p.boards.map((b) => (
                <button key={b.id} className="item" aria-selected={b.id === p.board?.id} onClick={() => (p.onPick(b.id), close())}>
                  <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="board" size={17} /></span>
                  <span className="min-w-0 flex-1 truncate font-medium">{b.name}</span>
                  {b.id === p.board?.id && <Icon name="check" size={18} className="text-[var(--brand-a)]" />}
                </button>
              ))}
              <button className="item text-[var(--brand-a)]" onClick={() => (p.onNew(), close())}>
                <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="plus" size={18} /></span>
                <span className="font-semibold">Board anlegen</span>
              </button>
            </>
          )}
        </Popover>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {p.onShowDone && (
          <button className="chip" aria-pressed={!!p.showDone} onClick={p.onShowDone}>Erledigte</button>
        )}
        {p.onNewTask && (
          <button className="chip" onClick={p.onNewTask}><Icon name="plus" size={14} />Aufgabe</button>
        )}
        {p.board && (
          <Popover
            align="right"
            width={230}
            trigger={({ toggle, open }) => (
              <button className="iconbtn" aria-label="Board verwalten" aria-expanded={open} onClick={toggle}><Icon name="more" size={18} /></button>
            )}
          >
            {(close) => (
              <>
                <button className="item" onClick={() => (p.onAddColumn?.(), close())}><Icon name="plus" size={18} />Spalte hinzufügen</button>
                <button className="item" onClick={() => (p.onRename?.(), close())}><Icon name="text" size={18} />Board umbenennen</button>
                <button className="item text-[var(--problem)]" onClick={() => (p.onDelete?.(), close())}><Icon name="trash" size={18} />Board löschen</button>
              </>
            )}
          </Popover>
        )}
      </div>
    </header>
  );
}

function Card(p: {
  task: Task;
  dragging: boolean;
  marked: boolean;
  lanes: { id: string | null; name: string }[];
  current: string | null;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverCard: (before: string) => void;
  onMove: (to: string | null) => void;
  /** Fehlt bei Unteraufgaben (nur eine Ebene) */
  onNewSubtask?: () => void;
}) {
  const { me, client } = useSync();
  const prog = taskProgress(client, p.task.id);
  const parent = p.task.parentId ? client.data.task.get(p.task.parentId) : null;
  const assignees = useMemo(
    () => me.members.filter((m) => p.task.assigneeIds.includes(m.userId)).slice(0, 3),
    [me.members, p.task.assigneeIds],
  );

  return (
    <div
      className={`board-card ${p.dragging ? 'dragging' : ''} ${p.marked ? 'marked' : ''}`}
      draggable
      onDragStart={(e: DragEvent) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', p.task.id);
        p.onDragStart();
      }}
      onDragEnd={p.onDragEnd}
      onDragOver={(e: DragEvent) => (e.preventDefault(), p.onDragOverCard(p.task.id))}
    >
      <button className="min-w-0 flex-1 text-left" onClick={p.onOpen}>
        <b className="block text-[14.5px] leading-snug font-semibold">{p.task.title}</b>
        {p.task.lastMessagePreview && <span className="mt-1 block truncate text-[12.5px] text-[var(--muted)]">{p.task.lastMessagePreview}</span>}
        <span className="mt-2 flex flex-wrap items-center gap-1.5">
          {p.task.status === 'erledigt' && <span className="tag t-erledigt">{statusText(p.task.status)}</span>}
          {p.task.dueAt && <span className="tag t-offen">{shortDate(p.task.dueAt)}</span>}
          {prog.subtasks.total > 0 && <span className="progchip" data-tip="Unteraufgaben erledigt"><Icon name="liste" size={12} />{prog.subtasks.done}/{prog.subtasks.total}</span>}
          {prog.checklist.total > 0 && <span className="progchip" data-tip="Checkliste abgehakt"><Icon name="check" size={12} />{prog.checklist.done}/{prog.checklist.total}</span>}
          {parent && <span className="truncate text-[11.5px] text-[var(--dim)]">Teil von {parent.title}</span>}
          {assignees.map((m) => (
            <Avatar key={m.userId} name={initials(m.name)} gradient={avatarGradient(m.userId)} src={userAvatarUrl(m.userId, m.avatarAt)} size={20} />
          ))}
        </span>
      </button>
      <Popover
        align="right"
        width={220}
        trigger={({ toggle, open }) => (
          <button className="iconbtn plain" style={{ width: 24, height: 24 }} aria-label={`Aktionen zu ${p.task.title}`} aria-expanded={open} onClick={toggle}>
            <Icon name="more" size={14} />
          </button>
        )}
      >
        {(close) => (
          <>
            {p.onNewSubtask && (
              <button className="item" onClick={() => (p.onNewSubtask!(), close())}>
                <Icon name="liste" size={18} />Unteraufgabe anlegen
              </button>
            )}
            {p.lanes.map((l) => (
              <button key={l.id ?? 'ohne'} className="item" aria-selected={l.id === p.current} disabled={l.id === p.current} onClick={() => (p.onMove(l.id), close())}>
                <Icon name={l.id === null ? 'inbox' : 'board'} size={18} />
                {l.id === null ? 'Vom Board nehmen' : `Nach „${l.name}“`}
              </button>
            ))}
          </>
        )}
      </Popover>
    </div>
  );
}

/** Kleiner Dialog für Namen (kein natives prompt). */
function NameDialog({ state, onClose, onSave }: {
  state: { kind: 'board' | 'column' | 'newBoard' | 'newColumn'; value: string };
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(state.value);
  const title = state.kind === 'newBoard' ? 'Neues Board' : state.kind === 'newColumn' ? 'Neue Spalte' : state.kind === 'board' ? 'Board umbenennen' : 'Spalte umbenennen';
  return (
    <Dialog title={title} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (name.trim()) onSave(name.trim());
        }}
      >
        <label className="field">Name<input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required autoFocus /></label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary">Speichern</button>
        </div>
      </form>
    </Dialog>
  );
}
